import React from 'react';
import { Form, Input, Button } from 'antd';
import Heading from 'components/heading/heading';
import { AuthWrapper } from 'pages/authentication/style';
import { UsersApi } from 'api/api';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import openNotificationWithIcon from 'utility/notification';
import { LocalStorageKeys, useLocalStorage } from 'hooks/useLocalStorage';
import styled from 'styled-components';

const CenteredContainer = styled.div<{ isOnBoarding: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: ${props => (props.isOnBoarding ? '100vh' : 'auto')};
`;

const FormContainer = styled.div<{ isOnBoarding: boolean }>`
  width: 100%;
  max-width: ${props => (props.isOnBoarding ? '400px' : '100%')};
  text-align: ${props => (props.isOnBoarding ? 'center' : 'left')};
  padding: ${props => (props.isOnBoarding ? '0' : '24px')};
`;

const Logo = styled.img`
  max-width: 200px;
  height: auto;
  margin-bottom: 24px;
`;

const SetNewPasswordPage = (props: any) => {
  const { t } = useTranslation();
  const { isOnBoardingMode = true } = props;
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [user, , , , updateNestedValue] = useLocalStorage(LocalStorageKeys.user, null);

  const usersApi = new UsersApi();

  const handleFormSubmit = async () => {
    setLoading(true);
    const payload = form.getFieldsValue();

    try {
      await usersApi.apiUsersSetNewPasswordPost({
        setNewPasswordRequest: payload,
      });
      openNotificationWithIcon(
        'success',
        t('set-new-password:notification.success.title', 'Success'),
        t('set-new-password:notification.success.description', 'Description'),
      );

      // Update shouldShowOnboarding field
      updateNestedValue(['shouldShowOnboarding'], false);
      navigate('/');
    } catch (error) {
      // Handle error
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthWrapper id="set-new-password-page" style={!isOnBoardingMode ? { marginLeft:'-22px', marginTop:'-48px', padding: '0', width: '50%', maxWidth: 1200 } : {}}>
      <CenteredContainer isOnBoarding={isOnBoardingMode}>
        <FormContainer isOnBoarding={isOnBoardingMode}>
          {isOnBoardingMode && <Logo src={require('../../static/img/org-logo/adriatic-project-logo.png')} alt="Logo" />}
          <Form name="setNewPassword" form={form} onFinish={handleFormSubmit} layout="vertical">
            {isOnBoardingMode && (
              <>
                <Heading as="h3">
                  {t('setNewPassword.title', 'Welcome to')} <span className="color-secondary">{t('global:our-platform', 'our platform!')}</span>
                </Heading>
                <p style={{ marginBottom: 50 }}>
                  {t(
                    'setNewPassword.title-desc',
                    "Welcome to your new account! To ensure your security, please set a strong password that you'll use for future logins.",
                  )}
                </p>
              </>
            )}
            <Form.Item
              name="currentPassword"
              rules={[
                {
                  required: true,
                  message: t('setNewPassword.currentPasswordRequired', 'Current Password is required'),
                },
              ]}
              label={t('setNewPassword.currentPasswordLabel', 'Current password')}
              style={{ marginBottom: 25 }}
            >
              <Input.Password placeholder={t('setNewPassword.currentPasswordPlaceholder', 'Current password')} />
            </Form.Item>
            <Form.Item
              name="password"
              rules={[
                {
                  required: true,
                  message: t(
                    'setNewPassword.newPasswordRequired',
                    'The password must be at least 8 characters long and contain an uppercase letter, a lowercase letter, a digit, and a special character.',
                  ),
                  min: 8,
                  pattern: /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/,
                },
              ]}
              label={t('setNewPassword.newPasswordLabel', 'New password')}
            >
              <Input.Password placeholder={t('setNewPassword.newPasswordPlaceholder', 'New password')} />
            </Form.Item>
            <Form.Item
              name="confirmPassword"
              label={t('setNewPassword.confirmPasswordLabel', 'Confirm password')}
              rules={[
                {
                  message: t('setNewPassword.confirmPasswordRequired', 'Confirm password is required'),
                  required: true,
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(
                        t(
                          'setNewPassword.confirmPasswordMismatch',
                          'The new password that you entered does not match!',
                        ),
                      ),
                    );
                  },
                }),
              ]}
            >
              <Input.Password placeholder={t('setNewPassword.confirmPasswordPlaceholder', 'Confirm password')} />
            </Form.Item>
            <Form.Item style={{ marginTop: 50 }}>
              <Button className="btn-signin" htmlType="submit" loading={loading} type="primary" size="large">
                {t('setNewPassword.saveButton', 'Save')}
              </Button>
            </Form.Item>
          </Form>
        </FormContainer>
      </CenteredContainer>
    </AuthWrapper>
  );
};

export default SetNewPasswordPage;
